import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Loader2, Lock, RefreshCw, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import api from '../../utils/api';

/**
 * PayTR under Provider Integrations.
 *
 * Shows what is ACTUALLY true: PayTR is modelled from its official iFrame,
 * notification and platform-transfer documents, but TreatX holds no merchant
 * credentials, so nothing can transact. The card therefore never renders a
 * "connected" state — it renders the exact missing settings, who supplies them,
 * and which capabilities stay unavailable until then.
 *
 * No secret is ever displayed: the backend sends presence flags only.
 */
export const PayTRIntegrationCard = () => {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get('/clinic/paytr/readiness');
      setState({ loading: false, data, error: null });
    } catch (err) {
      setState({
        loading: false,
        data: null,
        error: err?.response?.data?.detail || t('provider.paytr.loadFailed'),
      });
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) {
    return (
      <Card data-testid="paytr-card-loading" className="min-w-0">
        <CardContent className="flex items-center gap-2 py-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('provider.paytr.loading')}
        </CardContent>
      </Card>
    );
  }

  if (state.error) {
    return (
      <Card data-testid="paytr-card-error" className="min-w-0">
        <CardContent className="py-6">
          <p className="text-sm text-rose-600" data-testid="paytr-error-message">{state.error}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={load} data-testid="paytr-retry-button">
            <RefreshCw className="mr-2 h-3 w-3" />{t('provider.paytr.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const d = state.data || {};
  const config = d.configuration || {};
  const caps = d.capabilities || {};
  const settings = config.settings || {};

  return (
    <Card className="min-w-0" data-testid="paytr-integration-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base md:text-lg">{t('provider.paytr.title')}</CardTitle>
            <CardDescription className="text-sm">{t('provider.paytr.subtitle')}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="paytr-environment-badge">
              {t(`provider.paytr.environment.${d.environment === 'live' ? 'live' : 'sandbox'}`)}
            </Badge>
            <Badge
              className={d.execution_enabled ? 'bg-emerald-600' : 'bg-slate-500'}
              data-testid="paytr-execution-badge"
            >
              {t(d.execution_enabled ? 'provider.paytr.executionEnabled' : 'provider.paytr.executionDisabled')}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {!d.execution_enabled && (
          <div
            className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3"
            data-testid="paytr-disabled-reason"
          >
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-900">{t('provider.paytr.disabledReason')}</p>
          </div>
        )}

        {/* Exact missing settings — never the values. */}
        <section data-testid="paytr-configuration-section">
          <h4 className="mb-2 text-sm font-medium text-slate-700">{t('provider.paytr.configurationTitle')}</h4>
          <ul className="space-y-1.5">
            {Object.entries(settings).map(([name, info]) => (
              <li
                key={name}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2"
                data-testid={`paytr-setting-${name}`}
              >
                <span className="flex min-w-0 items-center gap-2 font-mono text-xs text-slate-600">
                  {info.secret && <Lock className="h-3 w-3 shrink-0 text-slate-400" />}
                  <span className="truncate">{name}</span>
                </span>
                <span className="flex items-center gap-2">
                  {info.masked && <span className="font-mono text-xs text-slate-400">{info.masked}</span>}
                  <Badge variant={info.configured ? 'default' : 'destructive'} className="text-[11px]">
                    {t(info.configured ? 'provider.paytr.settingSet' : 'provider.paytr.settingMissing')}
                  </Badge>
                </span>
              </li>
            ))}
          </ul>
          {config.how_to_obtain && (
            <p className="mt-2 text-xs text-slate-500" data-testid="paytr-how-to-obtain">
              {config.how_to_obtain}
            </p>
          )}
        </section>

        {/* Steps only the platform operator can complete, outside TreatX. */}
        {(config.external_steps || []).length > 0 && (
          <section data-testid="paytr-external-steps">
            <h4 className="mb-2 text-sm font-medium text-slate-700">{t('provider.paytr.externalStepsTitle')}</h4>
            <ul className="space-y-2">
              {config.external_steps.map((step) => (
                <li key={step.step} className="flex gap-2 text-xs text-slate-600" data-testid={`paytr-step-${step.step}`}>
                  <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                  <span className="min-w-0">{step.detail}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Capability truth, per the registry — no optimistic rollup. */}
        <section data-testid="paytr-capabilities-section">
          <h4 className="mb-2 text-sm font-medium text-slate-700">{t('provider.paytr.capabilitiesTitle')}</h4>
          <ul className="space-y-1.5">
            {(caps.capabilities || []).map((cap) => (
              <li
                key={cap.capability}
                className="rounded border border-slate-200 px-3 py-2"
                data-testid={`paytr-capability-${cap.capability}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-xs font-medium text-slate-700">
                    {t(`provider.paytr.capability.${cap.capability}`, cap.capability)}
                  </span>
                  <Badge variant="outline" className="text-[11px]">
                    {t(`provider.paytr.status.${cap.status}`, cap.status)}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-slate-500">{cap.note}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* The documented marketplace model, stated so nobody asks a provider
            for credentials PayTR does not issue to providers. */}
        <section
          className="rounded-md border border-slate-200 bg-slate-50 p-3"
          data-testid="paytr-payee-model"
        >
          <h4 className="mb-1 text-sm font-medium text-slate-700">{t('provider.paytr.payeeModelTitle')}</h4>
          <p className="text-xs text-slate-600">{caps.provider_credentials_note}</p>
          <p className="mt-2 text-xs text-slate-600" data-testid="paytr-settlement-currency-note">
            {t('provider.paytr.settlementCurrencyNote', {
              collection: (caps.documented_charge_currencies || []).join(', '),
              settlement: (caps.settlement_currencies || []).join(', '),
            })}
          </p>
          {(d.payee_model?.provider_tr_ibans || []).length === 0 && (
            <p className="mt-2 text-xs text-amber-700" data-testid="paytr-no-tr-iban">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              {t('provider.paytr.noTrIban')}
            </p>
          )}
        </section>

        {(d.blockers || []).length > 0 && (
          <section data-testid="paytr-blockers">
            <h4 className="mb-2 text-sm font-medium text-slate-700">{t('provider.paytr.blockersTitle')}</h4>
            <ul className="space-y-1">
              {d.blockers.map((b) => (
                <li key={b} className="text-xs text-slate-600" data-testid={`paytr-blocker-${b}`}>
                  • {t(`provider.paytr.blocker.${b}`, b)}
                </li>
              ))}
            </ul>
          </section>
        )}

        <Button variant="outline" size="sm" onClick={load} data-testid="paytr-refresh-button">
          <RefreshCw className="mr-2 h-3 w-3" />{t('provider.paytr.refresh')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default PayTRIntegrationCard;
